import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismIcon } from '@mylighthouse/prism-ember';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import {
  SMART_INSIGHT_PRIORITIES,
  SMART_INSIGHT_PRIORITY_ICONS,
  SMART_INSIGHT_PRIORITY_TRANSLATIONS,
  type SmartInsightPriority,
} from 'lumen/models/smart-insights/smart-insight-day';

import styles from './badge-priority.module.css';

interface GeneralSmartInsightsBadgePrioritySignature {
  Args: {
    iconOnly?: boolean;
    priority: SmartInsightPriority;
  };
}

export default class GeneralSmartInsightsBadgePriority extends Component<GeneralSmartInsightsBadgePrioritySignature> {
  @service declare intl: Services['intl'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get backgroundColorClass() {
    const { priority } = this.args;

    switch (priority) {
      case SMART_INSIGHT_PRIORITIES.HIGH:
        return styles['badge-high-priority'];
      case SMART_INSIGHT_PRIORITIES.MEDIUM:
        return styles['badge-medium-priority'];
      case SMART_INSIGHT_PRIORITIES.LOW:
        return styles['badge-low-priority'];
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get iconColor() {
    const { priority } = this.args;

    switch (priority) {
      case SMART_INSIGHT_PRIORITIES.HIGH:
        return 'default-inverse';
      case SMART_INSIGHT_PRIORITIES.MEDIUM:
        return 'on-intelligence';
      case SMART_INSIGHT_PRIORITIES.LOW:
        return 'on-intelligence';
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get iconName() {
    const { priority } = this.args;

    return SMART_INSIGHT_PRIORITY_ICONS[priority];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get label() {
    const { priority } = this.args;

    return this.intl.t(SMART_INSIGHT_PRIORITY_TRANSLATIONS[priority]);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get textColorClass() {
    const { priority } = this.args;

    switch (priority) {
      case SMART_INSIGHT_PRIORITIES.HIGH:
        return 'text-inverse';
      case SMART_INSIGHT_PRIORITIES.MEDIUM:
        return 'text-intelligence-emphasis';
      case SMART_INSIGHT_PRIORITIES.LOW:
        return 'text-intelligence-emphasis';
    }
  }

  <template>
    <div
      class="rounded-pill flex h-[20px] w-fit flex-row items-center justify-center gap-100 px-200
        {{this.backgroundColorClass}}"
      data-test-id="badge-priority"
    >
      {{#if @iconOnly}}
        <EmberTooltip @side="top">
          {{this.label}}
        </EmberTooltip>
      {{/if}}

      <PrismIcon @color={{this.iconColor}} @name={{this.iconName}} />

      {{#unless @iconOnly}}
        <div
          class="text-200--regular {{this.textColorClass}}"
        >{{this.label}}</div>
      {{/unless}}
    </div>
  </template>
}
